import { getCsrfToken } from "../util/getCsrfToken";

const API_BASE_URL = "/api";

interface ErrorCode {
  code: number;
}

export function isErrorCode(error: string | ErrorCode): error is ErrorCode {
  return !!(typeof error !== "string" && error["code"]);
}

export type ApiError = string | ErrorCode;

export function getApiErrorId(error: ApiError): string {
  return isErrorCode(error) ? `${error.code}` : error;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  statusCode: number;
  errors: ApiError[];
}

interface LoginResponse {
  zeemee_api_token: string;
  chat_api_token: string;
  chat_ws_token: string;
  csrf_token: string;
}

interface ValidateEmailResponse {
  sso: string | null;
  two_factor_authentication_required: boolean;
}

interface ChangePasswordResponse {
  message: string | null;
}

interface ResetPasswordResponse {
  message: string | null;
}

interface FetchApiTokenResponse {
  zeemee_api_token: string;
  chat_api_token: string;
  chat_ws_token: string;
}

interface LogoutResponse {
  csrf_token: string;
}

export enum AuthState {
  LOADING_TOKEN = "LOADING_TOKEN",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  AUTHORIZED = "AUTHORIZED",
  UNAUTHORIZED = "UNAUTHORIZED",
  SESSION_TIMEOUT = "SESSION_TIMEOUT",
  PENDING_TWO_FACTOR = "PENDING_TWO_FACTOR",
}

export type LogoutListener = () => void;

export class AuthApiClient {
  public zeemeeApiToken: string;
  public chatApiToken: string;
  public chatWsToken: string;

  private csrfToken: string | null;
  private authStateListener: ((newAuthState: AuthState) => void) | null;
  private logoutListeners: LogoutListener[];
  #authState: AuthState;

  constructor(csrfToken: string | null = null) {
    this.zeemeeApiToken = "";
    this.chatApiToken = "";
    this.chatWsToken = "";
    this.#authState = AuthState.LOADING_TOKEN;
    this.authStateListener = null;
    this.csrfToken = csrfToken;
    this.logoutListeners = [];
  }

  init() {
    // initial fetch of zeemee_api_token
    this.fetchApiToken().then((response) => {
      this.zeemeeApiToken = response.data?.zeemee_api_token || "";
      this.chatApiToken = response.data?.chat_api_token || "";
      this.chatWsToken = response.data?.chat_ws_token || "";
      this.authState = response.success
        ? AuthState.AUTHORIZED
        : AuthState.UNAUTHORIZED;
    });
  }

  set authState(state: AuthState) {
    this.#authState = state;
    if (this.authStateListener) this.authStateListener(state);
  }

  get authState() {
    return this.#authState;
  }

  setAuthStateListener(fn: (newAuthState: AuthState) => void) {
    this.authStateListener = fn;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}) {
    const method = options.method || "GET";
    const config = {
      ...options,
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken || "",
        ...(options.headers || {}),
      },
    };
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, config);

    const apiResponse: ApiResponse<T> = {
      success: response.ok,
      errors: [],
      statusCode: response.status,
    };

    const body = await this.getResponseBody(response);
    if (body.errors) apiResponse.errors = body.errors;
    apiResponse.data = body;
    return apiResponse;
  }

  private async getResponseBody(response: Response) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const text = await response.text();
      return text ? JSON.parse(text) : {};
    }
    return {};
  }

  validateEmail = (email: string) =>
    this.request<ValidateEmailResponse>("/login/preflight.json", {
      method: "POST",
      body: JSON.stringify({ user: { email } }),
    });

  async login(email: string, password: string, requiresTwoFactor = false) {
    if (requiresTwoFactor) {
      // For 2FA users, we need to initiate 2FA first, then they'll verify with OTP later
      const response = await this.request(
        "/login/initiate_two_factor_authentication.json",
        {
          method: "POST",
          body: JSON.stringify({ user: { email, password } }),
        },
      );

      if (response.success) {
        this.authState = AuthState.PENDING_TWO_FACTOR;
      }
      return response;
    } else {
      // Normal login flow for non-2FA users
      const response = await this.request<LoginResponse>("/login.json", {
        method: "POST",
        body: JSON.stringify({ user: { email, password } }),
      });

      if (response.success) {
        this.zeemeeApiToken = response.data?.zeemee_api_token || "";
        this.chatApiToken = response.data?.chat_api_token || "";
        this.chatWsToken = response.data?.chat_ws_token || "";
        this.authState = AuthState.AUTHORIZED;
        this.csrfToken = response.data?.csrf_token || null;
      }
      return response;
    }
  }

  async verifyOtp(email: string, otp: string) {
    const response = await this.request<LoginResponse>("/login.json", {
      method: "POST",
      body: JSON.stringify({ user: { email, code: otp } }),
    });

    if (response.success) {
      this.zeemeeApiToken = response.data?.zeemee_api_token || "";
      this.chatApiToken = response.data?.chat_api_token || "";
      this.chatWsToken = response.data?.chat_ws_token || "";
      this.csrfToken = response.data?.csrf_token || null;
      this.authState = AuthState.AUTHORIZED;
    }
    return response;
  }

  async logout() {
    const response = await this.request<LogoutResponse>("/logout.json", {
      method: "DELETE",
    });
    this.zeemeeApiToken = "";
    this.chatApiToken = "";
    this.chatWsToken = "";
    this.csrfToken = response.data?.csrf_token || null;
    this.onLogout();
    if (response.success) {
      this.authState = AuthState.UNAUTHORIZED;
    } else {
      this.authState = AuthState.SESSION_TIMEOUT;
    }
    return response;
  }

  resetPassword(email: string) {
    return this.request<ResetPasswordResponse>("/reset_password.json", {
      method: "POST",
      body: JSON.stringify({ user: { email } }),
    });
  }

  changePassword(
    password: string,
    passwordConfirmation: string,
    resetPasswordToken: string,
  ) {
    return this.request<ChangePasswordResponse>("/update_password.json", {
      method: "POST",
      body: JSON.stringify({
        user: {
          password,
          password_confirmation: passwordConfirmation,
          reset_password_token: resetPasswordToken,
        },
      }),
    });
  }

  async fetchApiToken() {
    const response = await this.request<FetchApiTokenResponse>(
      "/generate_tokens.json",
      {
        method: "POST",
      },
    );
    if (!response.success) {
      // user is no longer logged in...
      this.onLogout();
    }
    return response;
  }

  async refreshApiTokens() {
    const response = await this.fetchApiToken();
    if (response.statusCode === 401) {
      this.authState = AuthState.SESSION_TIMEOUT;
    } else {
      this.zeemeeApiToken = response.data?.zeemee_api_token || "";
      this.chatApiToken = response.data?.chat_api_token || "";
      this.chatWsToken = response.data?.chat_ws_token || "";
    }
    return response;
  }

  addLogoutListener(fn: LogoutListener) {
    this.logoutListeners.push(fn);
  }

  private onLogout() {
    for (const listner of this.logoutListeners) {
      listner();
    }
  }
}

export const authApiClient = new AuthApiClient(getCsrfToken());
